// src/components/Calendar/TaskDateModal.jsx
import React, { useState, useEffect } from "react";
import { Modal, List, Tag, Avatar, Spin, Empty, Typography, Space } from "antd";
import {
  ClockCircleOutlined,
  UserOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  CalendarOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
} from "@ant-design/icons";
import { getTasksByDate } from "@/api/calendarApi";
import "./TaskDateModal.css";
import { useNavigate } from "react-router-dom";

const { Text, Paragraph } = Typography;

const TaskDateModal = ({ visible, onClose, selectedDate }) => {
  const [tasks, setTasks] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (visible && selectedDate) {
      fetchDataForDate();
    }
  }, [visible, selectedDate]);

  const fetchDataForDate = async () => {
    setLoading(true);
    console.log('TaskDateModal - Olingan selectedDate:', selectedDate); // Debug uchun
    try {
      const data = await getTasksByDate(selectedDate);
      console.log('TaskDateModal - API javob:', data); // Debug uchun
      setTasks(data.tasks || []);
      setEvents(data.events || []);
    } catch (error) {
      console.error("Error fetching calendar details:", error);
      setTasks([]);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      pending: {
        icon: <ClockCircleOutlined />,
        text: "Kutilmoqda",
        tagColor: "#faad14",
      },
      in_progress: {
        icon: <SyncOutlined spin />,
        text: "Jarayonda",
        tagColor: "#1890ff",
      },
      submitted: {
        icon: <FileTextOutlined />,
        text: "Yuborilgan",
        tagColor: "#fa8c16",
      },
      approved: {
        icon: <CheckCircleOutlined />,
        text: "Tasdiqlangan",
        tagColor: "#52c41a",
      },
      rejected: {
        icon: <CloseCircleOutlined />,
        text: "Rad etilgan",
        tagColor: "#f5222d",
      },
    };
    return configs[status] || configs.pending;
  };

  const getEventStatusConfig = (status) => {
    const configs = {
      upcoming: { color: "blue", text: "Kelgusi" },
      ongoing: { color: "orange", text: "Davom etmoqda" },
      completed: { color: "green", text: "Yakunlangan" },
      overdue: { color: "red", text: "Muddati o'tgan" },
    };
    return configs[status] || { color: "default", text: status || "Noma'lum" };
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";

    // Agar dateStr allaqachon string formatda bo'lsa (YYYY-MM-DD)
    if (typeof dateStr === 'string' && dateStr.includes('-')) {
      const parts = dateStr.split('-');
      if (parts.length === 3) {
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10);
        const day = parseInt(parts[2], 10);

        const months = [
          'Yanvar', 'Fevral', 'Mart', 'Aprel', 'May', 'Iyun',
          'Iyul', 'Avgust', 'Sentabr', 'Oktabr', 'Noyabr', 'Dekabr'
        ];

        if (month >= 1 && month <= 12) {
          return `${day} ${months[month - 1]}, ${year}`;
        }
      }
    }

    return dateStr;
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      // Agar sana noto'g'ri bo'lsa
      if (isNaN(date.getTime())) return "";

      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    } catch (error) {
      return "";
    }
  };

  const handleTaskClick = (task) => {
    navigate(`/tasks/detail/${task.id}`);
    onClose();
  };

  const handleEventClick = (eventItem) => {
    navigate(`/events/detail/${eventItem.id}`);
    onClose();
  };

  const getUserName = (user) => {
    if (!user) return "Biriktirilgan: Yo'q";
    return user.name || `${user.first_name || ""} ${user.surname || ""}`.trim() || "Noma'lum";
  };

  const groupedTasks = tasks.reduce((acc, task) => {
    const status = task.status;
    if (!acc[status]) {
      acc[status] = [];
    }
    acc[status].push(task);
    return acc;
  }, {});

  const statusOrder = ["rejected", "pending", "in_progress", "submitted", "approved"];
  const hasTasks = tasks.length > 0;
  const hasEvents = events.length > 0;

  return (
    <Modal

      title={
        <Space>
          <CalendarOutlined />
          <span>{formatDate(selectedDate)} sanasidagi ma'lumotlar</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={920}
      className="task-date-modal"
    >
      {loading ? (
        <div className="modal-loading">
          <Spin size="large" tip="Ma'lumotlar yuklanmoqda..." />
        </div>
      ) : (
        <div className="tasks-container">
          <div className="tasks-summary">
            <Space size={16}>
              <Text type="secondary">Topshiriqlar: {tasks.length}</Text>
              <Text type="secondary">Tadbirlar: {events.length}</Text>
            </Space>
          </div>

          {hasEvents && (
            <div className="events-section">
              <div className="status-header">
                <Tag color="#597ef7" icon={<CalendarOutlined />}>
                  Tadbirlar ({events.length})
                </Tag>
              </div>

              <List
                dataSource={events}
                renderItem={(eventItem) => {
                  const statusConfig = getEventStatusConfig(eventItem.status);
                  const isRead = eventItem.is_read === true;
                  const readLabel = isRead ? "O'qilgan" : "O'qilmagan";
                  const readColor = isRead ? "green" : "red";

                  return (
                    <List.Item

                      className="event-item"
                      onClick={() => handleEventClick(eventItem)}
                      style={{ cursor: "pointer" }}
                    >
                      <List.Item.Meta
                        title={
                          <Space direction="vertical" size={2}>
                            <Text strong>{eventItem.name}</Text>
                            <Space size={8}>
                              <Tag color={statusConfig.color}>{statusConfig.text}</Tag>
                              {eventItem.is_read !== null && (
                                <Tag color={readColor} icon={isRead ? <EyeOutlined /> : <EyeInvisibleOutlined />}>
                                  {readLabel}
                                </Tag>
                              )}
                            </Space>
                          </Space>
                        }
                        description={
                          <Space direction="vertical" size={4}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              <ClockCircleOutlined style={{ marginRight: 4 }} />
                              {formatTime(eventItem.datetime)}
                            </Text>
                            {eventItem.tasks && eventItem.tasks.length > 0 && (
                              <div className="event-task-tags">
                                {eventItem.tasks.map((task) => (
                                  <Tag
                                    key={task.id}
                                    color={task.status === "completed" ? "green" : "orange"}
                                    className="event-task-tag"
                                  >
                                    {task.title}
                                  </Tag>
                                ))}
                              </div>
                            )}
                          </Space>
                        }
                      />
                    </List.Item>
                  );
                }}
              />
            </div>
          )}

          {hasTasks ? (
            statusOrder.map((status) => {
              const statusTasks = groupedTasks[status];
              if (!statusTasks || statusTasks.length === 0) return null;

              const statusConfig = getStatusConfig(status);

              return (
                <div key={status} className="status-group">
                  <div className="status-header">
                    <Tag color={statusConfig.tagColor} icon={statusConfig.icon}>
                      {statusConfig.text} ({statusTasks.length})
                    </Tag>
                  </div>

                  <List
                    dataSource={statusTasks}
                    renderItem={(task) => (
                      <List.Item
                        className="task-item"
                        onClick={() => handleTaskClick(task)}
                        style={{ cursor: "pointer", padding:20 }}
                      >
                        <List.Item.Meta
                          avatar={
                            <Avatar
                              src={task.assigned_user?.picture}
                              icon={<UserOutlined />}
                              size="large"
                            />
                          }
                          title={
                            <Space direction="vertical" size={0}>
                              <Text strong>{task.title}</Text>
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {getUserName(task.assigned_user)}
                              </Text>
                            </Space>
                          }
                          description={
                            <Paragraph ellipsis={{ rows: 2 }} style={{ marginBottom: 0 }}>
                              {task.description || "Tavsif yo'q"}
                            </Paragraph>
                          }
                        />
                        <div className="task-meta">
                          {task.deadline ? (
                            <Space size={4}>
                              <ClockCircleOutlined style={{ fontSize: 12 }} />
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {formatTime(task.deadline)}
                              </Text>
                            </Space>
                          ) : (
                            <Tag color="default" icon={<ExclamationCircleOutlined />}>
                              Muddat yo'q
                            </Tag>
                          )}
                        </div>
                      </List.Item>
                    )}
                  />
                </div>
              );
            })
          ) : (
            <div className="tasks-empty">
              <Empty
                description={
                  hasEvents
                    ? "Bu sana uchun topshiriq mavjud emas"
                    : "Bu sana uchun topshiriq yoki tadbir topilmadi"
                }
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default TaskDateModal;